// client/src/components/vatCredit/VatCreditAttachmentList.js
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Download } from 'lucide-react';
import { downloadVatCreditAttachment, formatFileSize } from '../utils/attachmentUtils';

/**
 * Elenco (sola lettura) degli allegati di una richiesta Crediti IVA con
 * pulsante di download. Usato sia lato azienda che lato banca; il backend
 * applica il controllo di visibilità sul download.
 * sideBySide: allegati in box affiancati di uguale larghezza invece che in elenco.
 */
const VatCreditAttachmentList = ({ requestId, attachments, sideBySide = false }) => {
  const { t } = useTranslation();
  const [downloadingId, setDownloadingId] = useState(null);
  const [error, setError] = useState('');

  if (!requestId) return null;

  const docTypeLabel = (docType) => {
    if (docType === 'vat_declaration') return t('vatCredit.attachments.vatDeclaration');
    if (docType === 'refund_request_deposit') return t('vatCredit.attachments.refundRequestDeposit');
    return docType;
  };

  const handleDownload = async (attachment) => {
    setError('');
    setDownloadingId(attachment.id);
    try {
      await downloadVatCreditAttachment(requestId, attachment);
    } catch (err) {
      setError(t('vatCredit.attachments.downloadError'));
    } finally {
      setDownloadingId(null);
    }
  };

  const list = attachments || [];

  return (
    <div className="vat-credit-attachments">
      <h3>{t('vatCredit.attachments.sectionTitle')}</h3>

      {error && <div className="alert alert-danger">{error}</div>}

      {list.length === 0 ? (
        <p style={{ color: '#6c757d' }}>{t('vatCredit.attachments.none')}</p>
      ) : (
        <ul
          className={`attachment-list ${sideBySide ? 'vc-attachment-grid' : ''}`}
          style={{ listStyle: 'none', padding: 0, margin: 0 }}
        >
          {list.map((att) => (
            <li
              key={att.id}
              className={sideBySide ? 'vc-attachment-box' : undefined}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                ...(sideBySide ? {} : { padding: '10px 0', borderBottom: '1px solid #eee' })
              }}
            >
              <FileText size={18} color="#35507d" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{docTypeLabel(att.docType)}</div>
                <div style={{ fontSize: '13px', color: '#6c757d', wordBreak: 'break-all' }}>
                  {att.originalName}
                  {att.size ? ` · ${formatFileSize(att.size)}` : ''}
                </div>
              </div>
              <button
                type="button"
                className="btn btn-light"
                onClick={() => handleDownload(att)}
                disabled={downloadingId === att.id}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
              >
                <Download size={14} />
                {downloadingId === att.id ? '…' : t('vatCredit.attachments.download')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default VatCreditAttachmentList;
